import React, { useState, useEffect } from 'react';
import * as api from '../../utils/api';
import styles from './FundHistoryPage.module.css';
import MessageBox from '../../components/MessageBox';
import ModifyDepositModal from '../../components/ModifyDepositModal'; // Import ModifyDepositModal
import { FiCheckCircle, FiEye } from 'react-icons/fi';

function FundHistoryPage() {
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('deposit'); // 'deposit', 'withdrawal', or 'rewards'
  const [message, setMessage] = useState(null);
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [selectedTransactionForModification, setSelectedTransactionForModification] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState('');

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const { data } = await api.getTransactions();
        setTransactions(data);
      } catch (err) {
        setMessage({ type: 'error', text: 'Failed to fetch transaction history.' });
      }
    };

    fetchTransactions();
  }, []);

  const handleCloseMessage = () => {
    setMessage(null);
  };

  const handleCancel = async (txnId) => {
    try {
      const { data } = await api.cancelWithdrawal(txnId);
      setTransactions(transactions.map(txn => txn._id === txnId ? data : txn));
      setMessage({ type: 'success', text: 'Withdrawal cancelled successfully.' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to cancel withdrawal.' });
    }
  };

  const handleModifyDeposit = (transactionId) => {
    const transaction = transactions.find(txn => txn._id === transactionId);
    setSelectedTransactionForModification(transaction);
    setIsDepositModalOpen(true);
  };

  const handleDepositModalClose = () => {
    setIsDepositModalOpen(false);
    setSelectedTransactionForModification(null);
  };

  const handleDepositModified = (updatedTransaction) => {
    setTransactions(transactions.map(txn => txn._id === updatedTransaction._id ? updatedTransaction : txn));
    setMessage({ type: 'success', text: 'Deposit updated successfully.' });
    handleDepositModalClose();
  };
  
  const depositTransactions = transactions.filter(txn => txn.txnType === 'deposit' && txn.txnStatus !== 'Cancelled');
  const withdrawalTransactions = transactions.filter(txn => txn.txnType === 'withdraw');
  const rewardTransactions = transactions.filter(txn => 
    (txn.txnType === 'Margin' || txn.txnType === 'Adjustments') && 
    txn.txnStatus === 'Completed' && 
    txn.txnAmount > 0
  );

  const renderTable = (data) => (
        <table className={styles['fund-history-table']}>
          <thead>
            <tr>
              <th></th> {/* Empty header for the eye icon column */}
              <th>Date</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
              {data.map((txn) => (
              <tr key={txn._id}>
                <td className={styles['eye-icon-cell']}>
                  {txn.paymentProof && (
                    <FiEye 
                      className={styles['eye-icon']}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent row click if any
                        if (txn.paymentProof.toLowerCase().endsWith('.pdf')) {
                          window.open(txn.paymentProof, '_blank');
                        } else {
                          setModalImageUrl(txn.paymentProof);
                          setShowImageModal(true);
                        }
                      }}
                    />
                  )}
                </td>
                <td>
                  {new Date(txn.txnTime)
                  .toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })
                  .replace(',', '')}
                </td>
                <td>{txn.txnAmount}</td>
                <td>{txn.txnStatus}</td>
                <td className={styles['actions-cell']}>
                  {txn.txnType === 'deposit' && txn.txnStatus === 'Pending' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleModifyDeposit(txn._id); }}
                      className={styles['modify-btn']}
                    >
                      Modify
                    </button>
                  )}
                  {txn.txnType === 'withdraw' && txn.txnStatus === 'Pending' && (
                    <button onClick={(e) => { e.stopPropagation(); handleCancel(txn._id); }} className={styles['cancel-btn']}>Cancel</button>
                  )}
                  {(txn.txnStatus === 'Completed' || txn.txnStatus === 'approved') && (
                    <FiCheckCircle className={styles['completed-icon']} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );

      const renderRewards = (data) => (
        <div className={styles['rewards-grid']}>
          {data.map((txn) => (
            <div key={txn._id} className={styles['reward-card']}>
              <div className={styles['reward-image-container']} onClick={() => {
                setModalImageUrl(txn.paymentProof);
                setShowImageModal(true);
              }}>
                <img src={txn.paymentProof} alt="Reward Proof" className={styles['reward-image']} />
                <div className={styles['reward-badge']}>Completed</div>
              </div>
              <div className={styles['reward-content']}>
                <p className={styles['reward-text']}>
                  {txn.txnType === 'Margin' 
                    ? `You have recieved extra margin amount of Rs. ${txn.txnAmount}`
                    : `Your account is increased by Rs. ${txn.txnAmount} as per direction of partner advisory firm`
                  }
                </p>
                <span className={styles['reward-date']}>{new Date(txn.txnTime).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      );

      return (
        <div className={styles['fund-history-page']}>
          {message && <MessageBox message={message.text} type={message.type} onClose={handleCloseMessage} />}
          <h1>Fund History</h1>
          <div className={styles.tabs}>
            <button
              className={activeTab === 'deposit' ? styles.active : ''}
              onClick={() => setActiveTab('deposit')}
              >
                Deposit
              </button>
              <button
              className={activeTab === 'withdrawal' ? styles.active : ''}
              onClick={() => setActiveTab('withdrawal')}
              >
                Withdrawal
              </button>
              <button
              className={activeTab === 'rewards' ? styles.active : ''}
              onClick={() => setActiveTab('rewards')}
              >
                Rewards
              </button>
              </div>
              
              {activeTab === 'deposit' && (
                depositTransactions.length > 0 ? (
                  renderTable(depositTransactions)
                ) : (
                  <p className={styles['no-data']}>No deposit transactions found.</p>
                )
              )}
              
              {activeTab === 'withdrawal' && (
                withdrawalTransactions.length > 0 ? (
                  renderTable(withdrawalTransactions)
                ) : (
                <p className={styles['no-data']}>No withdrawal transactions found.</p>
              )
              )}

              {activeTab === 'rewards' && (
                rewardTransactions.length > 0 ? (
                  renderRewards(rewardTransactions)
                ) : (
                  <p className={styles['no-data']}>No rewards or adjustments found.</p>
                )
              )}
              
              {isDepositModalOpen && (
                <ModifyDepositModal
                  onClose={handleDepositModalClose}
                  transactionToModify={selectedTransactionForModification}
                  onDepositModified={handleDepositModified}
                />
              )}

            {showImageModal && (
                <div className={styles['image-modal-overlay']} onClick={() => setShowImageModal(false)}>
                    <div className={styles['image-modal-content']} onClick={(e) => e.stopPropagation()}>
                        <span className={styles['image-modal-close']} onClick={() => setShowImageModal(false)}>&times;</span>
                        <img src={modalImageUrl} alt="Full Size Payment Proof" className={styles['full-size-image']} />
                    </div>
                </div>
            )}
    </div>
  );
}

export default FundHistoryPage;